import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  Brain,
  Heart,
  Zap,
  Globe,
  Shield,
  Cpu,
  X,
  Activity,
  Stethoscope,
  CheckCircle2,
  Sparkles,
  Lock,
  Users,
  Pause,
  Play,
} from "lucide-react";
import { useEffect, useState } from "react";

const Index = () => {
  const [visionPanelOpen, setVisionPanelOpen] = useState(false);
  const [ethicsDialogOpen, setEthicsDialogOpen] = useState(false);
  const [currentFeatureSlide, setCurrentFeatureSlide] = useState(0);
  const [isFeatureAutoPlay, setIsFeatureAutoPlay] = useState(true);
  const features = [
    {
      icon: Brain,
      title: "AI-Powered Healthcare",
      image: "/feature_ai_healthcare.png",
      description:
        "Standard and agentic AI revolutionizing diagnostics, triage, and personalized patient care pathways.",
    },
    {
      icon: Activity,
      title: "Connected Devices",
      image: "/feature_connected_devices.png",
      description:
        "IoT-enabled medical devices for continuous, real-time health monitoring and data collection.",
    },
    {
      icon: Cpu,
      title: "Healthcare APIs",
      image: "/feature_healthcare_apis.png",
      description:
        "Robust, standards-compliant APIs that enable seamless integration and clinical data exchange.",
    },
    {
      icon: Stethoscope,
      title: "Clinical SaaS",
      image: "/feature_clinical_saas.png",
      description:
        "Cloud-based healthcare platforms designed for scalability, security, and accessibility.",
    },
    {
      icon: Globe,
      title: "Made in India",
      image: "/feature_made_in_india.png",
      description:
        "World-class health-tech, engineered locally and built to serve patients globally.",
    },
    {
      icon: Shield,
      title: "Affordable Quality",
      image: "/feature_affordable_quality.png",
      description:
        "International standards and rigor at prices accessible to every community we serve.",
    },
  ];

  const stats = [
    { value: "AWS Hosted", label: "Robust Architecture" },
    { value: "24/7", label: "Support System" },
    { value: "HIPAA-aligned", label: "Data Protection" },
    { value: "Global", label: "Quality Standards" },
  ];

  const trustChips = [
    { icon: Lock, label: "Privacy-First" },
    { icon: Shield, label: "Enterprise-Grade Security" },
    { icon: CheckCircle2, label: "Clinically Aligned" },
    { icon: Users, label: "Built with Clinicians" },
  ];

  useEffect(() => {
    if (!isFeatureAutoPlay) {
      return;
    }

    const autoSlideTimer = setInterval(() => {
      setCurrentFeatureSlide((prev) => (prev + 1) % features.length);
    }, 4200);

    return () => clearInterval(autoSlideTimer);
  }, [features.length, isFeatureAutoPlay]);

  return (
    <>
      <Layout>
        {/* ─────────────────  HERO  ───────────────── */}
        <section className="relative min-h-[92vh] flex items-center overflow-hidden">
          <div
            className="absolute inset-0 z-0"
            style={{
              backgroundImage: `var(--gradient-hero), url('/bg_main.jpg')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          {/* Subtle dotted grid overlay for tech feel */}
          <div
            className="absolute inset-0 z-[1] opacity-30 mix-blend-overlay"
            style={{
              backgroundImage:
                "radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />
          {/* Glow accent */}
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-secondary/20 blur-3xl z-[1]" />
          <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] rounded-full bg-primary/30 blur-3xl z-[1]" />

          <div className="container mx-auto px-4 relative z-10 pb-14 md:pb-0">
            <div className="max-w-5xl">
              {/* Eyebrow badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6 animate-fade-in">
                <span className="relative flex h-2 w-2">
                  <span className="animate-pulse-soft absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-secondary" />
                </span>
                <span className="text-xs font-medium text-white tracking-wide uppercase">
                  Connected Healthcare Intelligence Platform
                </span>
              </div>

              <h1
                className="text-5xl md:text-6xl font-bold text-white mb-6 animate-fade-in delay-100 leading-[1.05] tracking-tight"
                data-testid="text-hero-title"
              >
                Digital Healthcare Ecosystem for {" "}
                <span className="bg-gradient-to-r from-secondary to-cyan-300 bg-clip-text text-transparent">
                  Modern India
                </span>
              </h1>

              <p className="text-lg md:text-2xl text-white/85 mb-8 max-w-2xl leading-relaxed animate-fade-in delay-200">
                Wellnext Technologies unifies healthcare providers, facilitators, clinics, diagnostics, and patients through AI-powered connected healthcare systems.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in delay-300">
                <Button asChild variant="outline" size="lg" className="bg-white border-[#264653] text-[#264653] hover:bg-white hover:text-[#000000]">
                  <Link to="/products" data-testid="button-explore-products-nav">Explore Products</Link>
                </Button>
                <Button
                  asChild
                  variant="default"
                  size="lg"
                  className="bg-cyan-900"
                >
                  <a href="mailto:corporate@wellnextek.com" data-testid="button-request-demo-nav">Request Demo</a>
                </Button>

              </div>

              {/* Trust chips */}
              <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3 animate-fade-in delay-500">
                {trustChips.map((chip) => {
                  const Icon = chip.icon;
                  return (
                    <div
                      key={chip.label}
                      className="flex items-center gap-2 text-white/75 text-sm"
                      data-testid={`chip-trust-${chip.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <Icon className="h-4 w-4 text-secondary" />
                      {chip.label}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Stats strip at bottom of hero */}
          <div className="absolute bottom-0 left-0 right-0 z-10">
            <div className="container mx-auto px-4">
              <div className="bg-white/10 backdrop-blur-xl border border-white/15 rounded-t-2xl">
                <div className="grid grid-cols-4 divide-x divide-white/10">
                  {stats.map((s) => (
                    <div
                      key={s.label}
                      className="px-1 py-2 md:px-4 md:py-5 text-center"
                      data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div className="text-xs sm:text-base md:text-xl lg:text-2xl font-bold text-white leading-tight">
                        {s.value}
                      </div>
                      <div className="text-[9px] sm:text-xs md:text-sm text-white/70 mt-0.5 md:mt-1 uppercase tracking-wide leading-tight">
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─────────────────  CAPABILITIES  ───────────────── */}
        <section className="py-24 bg-gradient-to-b from-muted/50 to-background relative overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-semibold uppercase tracking-wider mb-4">
                <Sparkles className="h-3.5 w-3.5" />
                Capabilities
              </div>
              <h2 className="text-4xl md:text-5xl font-bold leading-tight tracking-tight mb-5">
                Transforming Healthcare{" "}
                <span className="text-gradient-primary">Through Technology</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                A comprehensive suite of intelligent health-tech solutions —
                empowering providers, supporting clinicians, and improving
                patient outcomes around the globe.
              </p>
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="relative overflow-hidden rounded-3xl">
                <div
                  className="flex transition-transform duration-700 ease-out"
                  style={{ transform: `translateX(-${currentFeatureSlide * 100}%)` }}
                >
                  {features.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <div key={index} className="w-full flex-shrink-0">
                        <Card className="group overflow-hidden rounded-3xl border border-border/70 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.07)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.13)] transition-all duration-500">
                          {/* Gradient accent bar */}
                          <div className="h-[3px] w-full bg-gradient-to-r from-primary via-secondary to-cyan-400" />

                          <CardContent className="p-0">
                            {/* Mobile-only image strip */}
                            <div className="lg:hidden relative h-52 overflow-hidden">
                              <img
                                src={feature.image}
                                alt={feature.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-white via-white/10 to-transparent" />
                            </div>

                            {/* Desktop split layout */}
                            <div className="grid lg:grid-cols-[1fr_420px] min-h-[440px]">
                              {/* Left: content */}
                              <div className="p-8 md:p-10 flex flex-col">
                                {/* Status badge */}
                                <div className="flex items-center gap-2 mb-7">
                                  <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                                  <span className="text-xs font-semibold tracking-[0.18em] uppercase text-muted-foreground">
                                    Live Healthcare Module
                                  </span>
                                </div>

                                {/* Icon + title */}
                                <div className="flex items-center gap-4 mb-6">
                                  <div className="h-14 w-14 shrink-0 rounded-2xl bg-gradient-to-br from-primary to-secondary grid place-items-center shadow-lg shadow-primary/25 group-hover:scale-105 transition-transform duration-300">
                                    <Icon className="h-7 w-7 text-white" />
                                  </div>
                                  <div>
                                    <h3 className="text-2xl md:text-[28px] font-bold text-foreground tracking-tight leading-tight">
                                      {feature.title}
                                    </h3>
                                    <p className="text-sm text-primary font-medium mt-0.5">
                                      Intelligent Healthcare Infrastructure
                                    </p>
                                  </div>
                                </div>

                                {/* Description */}
                                <p className="text-[16px] leading-[1.75] text-muted-foreground mb-auto">
                                  {feature.description}
                                </p>

                                {/* Metrics */}
                                <div className="grid grid-cols-3 gap-3 mt-8 pt-7 border-t border-border/60">
                                  <div className="rounded-xl bg-primary/5 border border-primary/10 p-3 text-center">
                                    <p className="text-xl font-bold text-primary">98%</p>
                                    <p className="text-[11px] text-muted-foreground mt-0.5 font-medium">Efficiency</p>
                                  </div>
                                  <div className="rounded-xl bg-secondary/5 border border-secondary/10 p-3 text-center">
                                    <p className="text-xl font-bold text-secondary">24/7</p>
                                    <p className="text-[11px] text-muted-foreground mt-0.5 font-medium">Coordination</p>
                                  </div>
                                  <div className="rounded-xl bg-primary/5 border border-primary/10 p-3 text-center">
                                    <p className="text-xl font-bold text-primary">AI</p>
                                    <p className="text-[11px] text-muted-foreground mt-0.5 font-medium">Assisted</p>
                                  </div>
                                </div>
                              </div>

                              {/* Right: image (desktop only) */}
                              <div className="hidden lg:block relative overflow-hidden border-l border-border/40">
                                <img
                                  src={feature.image}
                                  alt={feature.title}
                                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                                />
                                {/* Subtle left-edge fade to blend with content */}
                                <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-white to-transparent pointer-events-none" />
                                {/* Floating status pill */}
                                <div className="absolute bottom-5 left-5 flex items-center gap-2 bg-white/90 backdrop-blur-md rounded-xl px-3 py-2 shadow-lg border border-white/60">
                                  <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                                  <span className="text-xs font-semibold text-foreground">AI Sync Active</span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-8 flex items-center justify-center gap-2" aria-label="Feature carousel indicators">
                {features.map((feature, index) => (
                  <button
                    key={feature.title}
                    type="button"
                    onClick={() => setCurrentFeatureSlide(index)}
                    className={`h-2.5 rounded-full transition-all duration-300 ${currentFeatureSlide === index
                        ? "w-10 bg-primary"
                        : "w-2.5 bg-primary/30 hover:bg-primary/50"
                      }`}
                    aria-label={`Go to ${feature.title}`}
                  />
                ))}

                <button
                  type="button"
                  onClick={() => setIsFeatureAutoPlay((prev) => !prev)}
                  className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full border border-primary/25 text-primary/80 hover:bg-primary/10 hover:text-primary transition-colors"
                  aria-label={isFeatureAutoPlay ? "Pause carousel autoplay" : "Play carousel autoplay"}
                  title={isFeatureAutoPlay ? "Pause" : "Play"}
                >
                  {isFeatureAutoPlay ? (
                    <Pause className="h-3 w-3" />
                  ) : (
                    <Play className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ─────────────────  AI VISION  ───────────────── */}
        <section className="py-24 bg-gradient-to-br from-primary/[0.04] via-secondary/[0.04] to-background relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-40 bg-grid pointer-events-none"
            aria-hidden
          />

          <div className="container mx-auto px-4 relative">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
                  <Brain className="h-3.5 w-3.5" />
                  AI Platform
                </div>
                <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight tracking-tight">
                  Applications of AI at the{" "}
                  <span className="text-gradient-primary">Heart of Our SaaS Platform</span>
                </h2>
                <div className="space-y-5 text-[17px] leading-relaxed text-muted-foreground">
                  <p>
                    We're building a connected healthcare platform that reduces
                    everyday friction between patients and medical services.
                    Patients describe concerns in simple terms, discover the
                    most relevant doctors, clinics, or diagnostics through an
                    AI-assisted search, and book appointments seamlessly —
                    reaching the right care earlier and with greater confidence.
                  </p>
                  <p>
                    On the provider side, our platform streamlines clinical and
                    operational workflows. Clinics and diagnostic centers
                    benefit from automated rostering, appointment-flow
                    optimization, and coordinated scheduling, while doctors
                    receive assistance with structured documentation,
                    diagnostic support, and prescription drafting.
                  </p>
                  <p>
                    At the core is an AI intelligence layer that connects
                    patient intent, provider availability, and clinical
                    workflows into a single system — supporting medical
                    decision-making rather than replacing it.
                  </p>
                </div>
                <Button
                  size="lg"
                  variant="default"
                  disabled
                  className="text-base mt-8 h-12 px-6"
                  data-testid="button-discover-more"
                >
                  Discover More
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>

              <div className="relative">
                {/* Decorative blobs */}
                <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-secondary/20 blur-3xl" />
                <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-primary/20 blur-3xl" />

                <div className="relative aspect-square rounded-2xl bg-gradient-to-br from-primary to-secondary p-[2px] shadow-elegant">
                  <div className="w-full h-full rounded-[14px] bg-background flex items-center justify-center overflow-hidden">
                    <img
                      src="/AI-multi-party-connectivity.png"
                      alt="AI Interaction"
                      className="w-full h-full object-contain rounded-[14px]"
                    />
                  </div>

                  {/* Floating "live" indicator */}
                  <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-md shadow-md text-xs font-semibold">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-pulse-soft absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                    </span>
                    <span className="text-foreground">AI Live</span>
                  </div>

                  {/* Floating metric badge */}
                  <div className="hidden md:block absolute -bottom-5 -left-5 bg-background border border-border rounded-xl shadow-elegant px-4 py-3 animate-float">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-secondary to-primary grid place-items-center">
                        <Heart className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">
                          Patient Experience
                        </div>
                        <div className="text-sm font-semibold">
                          Seamless & Personalized
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─────────────────  MADE IN INDIA  ───────────────── */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold uppercase tracking-wider mb-4">
                <Globe className="h-3.5 w-3.5" />
                Global Vision
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight tracking-tight">
                Made in India,{" "}
                <span className="text-gradient-primary">Built for the World</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-4 leading-relaxed">
                Our vision extends beyond borders. We develop world-class
                health-tech products in India, ensuring international quality
                standards at prices that make advanced healthcare accessible to all.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                By combining local innovation with global best practices,
                we're proving that affordable excellence isn't just possible —
                it's our promise.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[
                {
                  value: "100%",
                  label: "Made in India",
                  description: "End-to-end engineered locally",
                  color: "from-primary to-secondary",
                },
                {
                  value: "Global",
                  label: "Quality Standards",
                  description: "International compliance & rigor",
                  color: "from-secondary to-cyan-500",
                },
                {
                  value: "Affordable",
                  label: "For Everyone",
                  description: "Accessible to every community",
                  color: "from-accent to-orange-500",
                },
              ].map((stat) => (
                <Card
                  key={stat.label}
                  className="border-border/60 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1"
                  data-testid={`card-stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <CardContent className="p-7 text-center">
                    <div
                      className={`text-4xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent mb-2`}
                    >
                      {stat.value}
                    </div>
                    <div className="font-semibold text-foreground mb-1">
                      {stat.label}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {stat.description}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ─────────────────  CTA  ───────────────── */}
        <section className="py-20 px-0 sm:px-4">
          <div className="container mx-auto">
            <div className="relative overflow-hidden rounded-none sm:rounded-3xl bg-gradient-primary shadow-elegant">
              {/* Decorative elements */}
              <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
              <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-secondary/30 blur-3xl" />
              <div
                className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage:
                    "radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px)",
                  backgroundSize: "32px 32px",
                }}
              />

              <div className="relative px-5 py-12 sm:px-6 sm:py-16 md:px-16 md:py-24 text-center">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-5 leading-tight tracking-tight">
                  Ready to Transform Healthcare Together?
                </h2>
                <p className="text-base sm:text-lg md:text-xl text-white/85 mb-9 max-w-2xl mx-auto leading-relaxed">
                  Join us in our mission to make quality healthcare accessible
                  through innovative technology solutions.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button
                    asChild
                    size="lg"
                    className="w-full sm:w-auto text-base h-12 px-7 bg-white text-primary hover:bg-white/90 shadow-lg"
                    data-testid="button-get-in-touch"
                  >
                    <Link to="/support">
                      Get in Touch
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto text-base h-12 px-7 border-white/40 bg-white/5 backdrop-blur-md text-white hover:bg-white hover:text-primary"
                    onClick={() => setVisionPanelOpen(true)}
                    data-testid="button-cta-vision"
                  >
                    Mission & Vision Statement
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full sm:w-auto text-base h-12 px-7 border-white/40 bg-white/5 backdrop-blur-md text-white hover:bg-white hover:text-primary"
                    onClick={() => setEthicsDialogOpen(true)}
                    data-testid="button-values-ethics"
                  >
                    Values & Ethics
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>

      {/* Vision Panel Overlay */}
      {visionPanelOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-300"
          onClick={() => setVisionPanelOpen(false)}
        />
      )}

      {/* Vision Side Panel */}
      <div
        className={`fixed inset-0 md:inset-auto md:top-0 md:right-0 md:h-full md:w-[500px] bg-background shadow-2xl z-50 transform transition-transform duration-500 ease-in-out ${visionPanelOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        <div className="h-full flex flex-col p-8 overflow-y-auto">
          <div className="mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gradient-primary mb-2">
              Our Vision & Mission
            </h2>
            <div className="h-1 w-20 bg-gradient-to-r from-primary to-secondary rounded-full" />
          </div>

          <div className="mb-8">
            <h3 className="text-2xl font-semibold text-primary mb-4">Mission</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              At Wellnext Technologies, our mission is to transform healthcare
              through intelligent, connected, and data-driven innovation. We
              empower doctors, healthcare organizations, and individuals with
              technologies that simplify medical workflows, enhance
              decision-making, and promote preventive and personalized care.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              To build a healthier world where AI, innovation, and compassion
              unite to redefine the future of healthcare.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-2xl font-semibold text-secondary mb-4">Vision</h3>
            <p className="text-muted-foreground leading-relaxed">
              To build a healthier world where AI, innovation, and compassion
              unite to redefine the future of healthcare.
            </p>
          </div>

          <div className="mt-auto pt-6 flex justify-center">
            <Button
              onClick={() => setVisionPanelOpen(false)}
              variant="default"
              size="lg"
              className="min-w-[200px]"
            >
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* Values & Ethics Dialog Overlay */}
      {ethicsDialogOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity duration-300"
          onClick={() => setEthicsDialogOpen(false)}
        />
      )}

      <div
        className={`fixed inset-0 md:inset-auto md:top-0 md:right-0 md:h-full md:w-[500px] bg-background rounded-none md:rounded-l-2xl shadow-2xl z-50 transform transition-transform duration-500 ease-in-out ${ethicsDialogOpen ? "translate-x-0" : "translate-x-full"
          }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col p-6 overflow-y-auto">
          <div className="relative pb-4 border-b border-border">
            <h2 className="text-2xl md:text-3xl font-bold text-gradient-primary pr-10">
              Our Values & Ethics
            </h2>
            <button
              onClick={() => setEthicsDialogOpen(false)}
              className="absolute top-0 right-0 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="pt-6 space-y-5">
            {[
              {
                title: "Innovation with Purpose",
                body: "We innovate not for complexity, but for clarity — creating intelligent technologies that simplify healthcare, enhance accuracy, and improve lives.",
              },
              {
                title: "Integrity and Trust",
                body: "We value transparency, ethics, and accountability in every step — from how we handle health data to how we serve our partners and patients.",
              },
              {
                title: "Human-Centered Design",
                body: "Technology must serve people, not the other way around. Every solution we build is guided by empathy, accessibility, and real-world medical needs.",
              },
              {
                title: "Collaboration and Partnership",
                body: "We believe in co-creating the future of healthcare through collaboration — with doctors, organizations, technologists, and communities worldwide.",
              },
              {
                title: "Accessibility & Affordability",
                body: "We believe quality healthcare should be accessible to everyone, regardless of economic status, and we work to make our solutions affordable globally.",
              },
              {
                title: "Impact Through Intelligence",
                body: "Our goal is measurable transformation. By turning data into insight and insight into action, we aim to drive meaningful impact on individual and community health.",
              },
              {
                title: "Data Privacy & Security",
                body: "We maintain the strictest protocols to protect patient data, complying with international standards and regulations to safeguard sensitive health information.",
              },
            ].map((item) => (
              <div key={item.title}>
                <h3 className="text-lg font-semibold text-primary mb-2">
                  {item.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {item.body}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-auto pt-6 border-t border-border flex flex-col sm:flex-row justify-center gap-3">
            <Button
              onClick={() => setEthicsDialogOpen(false)}
              variant="outline"
              size="lg"
              className="min-w-[150px]"
            >
              Learn More
            </Button>
            <Button
              onClick={() => setEthicsDialogOpen(false)}
              variant="default"
              size="lg"
              className="min-w-[150px]"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Index;
